import React, { useState, useMemo, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import ImageUploadButton from '../../ImageUploadButton';
import { SnackbarContext } from '../../../components/snackBar/SnackbarContext';

const InputField = ({ label, name, type = 'text', icon, showPassword, onTogglePassword, isFocused, onFocus, onBlur, value, onChange }) => {
  console.log(`InputField ${name} render, isFocused: ${isFocused}`);
  return (
    <div style={{ marginBottom: '16px', width: '100%' }}>
      <label style={{
        display: 'block',
        marginBottom: '8px',
        fontSize: '14px',
        fontWeight: 500,
        color: '#9E9E9E',
      }}>{label}</label>
      <div style={{ position: 'relative' }}>
        {icon && (
          <span style={{
            position: 'absolute',
            left: '12px',
            top: '50%',
            transform: 'translateY(-50%)',
            color: '#9E9E9E',
            fontSize: '20px',
          }}>{icon}</span>
        )}
        <input
          type={type === 'password' ? (showPassword ? 'text' : 'password') : type}
          name={name}
          value={value}
          onChange={onChange}
          style={{
            width: '100%',
            padding: icon ? '14px 40px 14px 44px' : '14px 16px',
            borderRadius: '12px',
            border: `1px solid ${isFocused ? '#0017E7' : '#E0E0E0'}`,
            backgroundColor: isFocused ? '#FFFFFF' : '#F5F7FA',
            fontSize: '14px',
            fontFamily: 'inherit',
            transition: 'all 0.3s ease',
            outline: 'none',
            boxSizing: 'border-box',
            boxShadow: isFocused ? '0 6px 20px rgba(0, 23, 231, 0.12)' : 'none',
          }}
          onFocus={() => {
            console.log(`onFocus called for ${name}`);
            onFocus();
          }}
          onBlur={() => {
            console.log(`onBlur called for ${name}`);
            onBlur();
          }}
        />
        {type === 'password' && (
          <button
            type="button"
            onClick={onTogglePassword}
            style={{
              position: 'absolute',
              right: '12px',
              top: '50%',
              transform: 'translateY(-50%)',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: '#9E9E9E',
              fontSize: '20px',
              padding: '4px',
            }}
          >
            {showPassword ? '👁️' : '👁️‍🗨️'}
          </button>
        )}
      </div>
    </div>
  );
};

const SuperAdminProfileEdit = () => {
  const [form, setForm] = useState({
    username: '',
    email: '',
    first_name: '',
    last_name: '',
    address: '',
    city: '',
    country: '',
    postal_code: '',
    image: '',
    current_password: '',
    new_password: '',
    confirm_password: '',
  });

  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [focusedField, setFocusedField] = useState(null);

  const { showSnackbar } = useContext(SnackbarContext);
  const { t } = useTranslation();

  const colors = {
    primary: '#0017E7',
    accent: '#F4E1F7',
    surface: '#FFFFFF',
    text: '#1A1A1A',
    textLight: '#616161',
    textMuted: '#9E9E9E',
    border: '#E0E0E0',
    success: '#00C853',
    error: '#F44336',
  };

  const handleFocus = (name) => {
    console.log(`handleFocus called for ${name}`);
    setFocusedField(name);
  };
  const handleBlur = () => {
    console.log('handleBlur called');
    setFocusedField(null);
  };

  const formCompletionPercentage = useMemo(() => {
    const requiredFields = ['username', 'email', 'first_name', 'last_name'];
    const filledRequired = requiredFields.filter(field => form[field].trim() !== '').length;
    const optionalFields = ['address', 'city', 'country', 'postal_code', 'image'];
    const filledOptional = optionalFields.filter(field => form[field].trim() !== '').length;

    const requiredPercentage = (filledRequired / requiredFields.length) * 70;
    const optionalPercentage = (filledOptional / optionalFields.length) * 30;

    return Math.round(requiredPercentage + optionalPercentage);
  }, [form]);

  const getProgressColor = (progress) => {
    if (progress <= 25) return colors.error;
    if (progress <= 50) return '#FF9800';
    if (progress <= 75) return '#FFD700';
    return colors.success;
  };

  const handleChange = (e) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setLoading(true);
      const apiUrl = process.env.REACT_APP_API_URL;
      const token = localStorage.getItem('accessToken');
      const fd = new FormData();
      fd.append('image', file);
      const res = await fetch(`${apiUrl}authentication/superadmin/profile/upload-image/`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) {
        console.log('handleImageUpload error - raw data:', data);
        console.log('handleImageUpload error - data type:', typeof data);
        let errorMessage = 'Image upload failed';
        if (data && typeof data === 'object') {
          console.log('handleImageUpload error - data is object, keys:', Object.keys(data));
          if (data.detail) {
            console.log('handleImageUpload error - found detail:', data.detail);
            if (Array.isArray(data.detail)) {
              console.log('handleImageUpload error - detail is array, taking first element');
              errorMessage = data.detail[0];
            } else if (typeof data.detail === 'string') {
              console.log('handleImageUpload error - detail is string');
              // Check if it's a Python dict string representation
              if (data.detail.startsWith('{') && data.detail.includes('ErrorDetail')) {
                console.log('handleImageUpload error - detail appears to be Python dict string, parsing...');
                try {
                  // Extract ErrorDetail string value using regex
                  const errorDetailMatch = data.detail.match(/ErrorDetail\(string='([^']+)'/);
                  if (errorDetailMatch) {
                    errorMessage = errorDetailMatch[1];
                    console.log('handleImageUpload error - extracted from ErrorDetail:', errorMessage);
                  } else {
                    errorMessage = data.detail;
                  }
                } catch (parseError) {
                  console.log('handleImageUpload error - failed to parse ErrorDetail string:', parseError);
                  errorMessage = data.detail;
                }
              } else {
                errorMessage = data.detail;
              }
            } else if (typeof data.detail === 'object') {
              console.log('handleImageUpload error - detail is object, extracting message');
              errorMessage = data.detail.string || String(data.detail);
              console.log('handleImageUpload error - extracted message:', errorMessage);
            }
          } else {
            // Check for any error field
            console.log('handleImageUpload error - checking other error fields');
            const errorKeys = Object.keys(data);
            for (const key of errorKeys) {
              const fieldError = data[key];
              console.log(`handleImageUpload error - checking field ${key}:`, fieldError);
              if (fieldError) {
                if (Array.isArray(fieldError)) {
                  console.log(`handleImageUpload error - field ${key} is array, taking first element`);
                  errorMessage = fieldError[0];
                  break;
                } else if (typeof fieldError === 'string') {
                  console.log(`handleImageUpload error - field ${key} is string`);
                  errorMessage = fieldError;
                  break;
                } else if (typeof fieldError === 'object') {
                  console.log(`handleImageUpload error - field ${key} is object, extracting message`);
                  errorMessage = fieldError.string || String(fieldError);
                  console.log(`handleImageUpload error - extracted message from ${key}:`, errorMessage);
                  break;
                }
              }
            }
          }
        }
        console.log('handleImageUpload error - final errorMessage:', errorMessage);
        throw new Error(errorMessage);
      }
      setForm(prev => ({ ...prev, image: data.image_url || '' }));
      showSnackbar(t('adminProfile.snackbarMessage'), { severity: 'success' });
    } catch (err) {
      showSnackbar(err.message || 'Image upload failed', { severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const fetchProfile = async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const token = localStorage.getItem('accessToken');
      const res = await fetch(`${apiUrl}authentication/superadmin/profile/get/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) {
        console.log('fetchProfile error - raw data:', data);
        console.log('fetchProfile error - data type:', typeof data);
        let errorMessage = 'Failed to load profile';
        if (data && typeof data === 'object') {
          console.log('fetchProfile error - data is object, keys:', Object.keys(data));
          if (data.detail) {
            console.log('fetchProfile error - found detail:', data.detail);
            if (Array.isArray(data.detail)) {
              console.log('fetchProfile error - detail is array, taking first element');
              errorMessage = data.detail[0];
            } else if (typeof data.detail === 'string') {
              console.log('fetchProfile error - detail is string');
              // Check if it's a Python dict string representation
              if (data.detail.startsWith('{') && data.detail.includes('ErrorDetail')) {
                console.log('fetchProfile error - detail appears to be Python dict string, parsing...');
                try {
                  // Extract ErrorDetail string value using regex
                  const errorDetailMatch = data.detail.match(/ErrorDetail\(string='([^']+)'/);
                  if (errorDetailMatch) {
                    errorMessage = errorDetailMatch[1];
                    console.log('fetchProfile error - extracted from ErrorDetail:', errorMessage);
                  } else {
                    errorMessage = data.detail;
                  }
                } catch (parseError) {
                  console.log('fetchProfile error - failed to parse ErrorDetail string:', parseError);
                  errorMessage = data.detail;
                }
              } else {
                errorMessage = data.detail;
              }
            } else if (typeof data.detail === 'object') {
              console.log('fetchProfile error - detail is object, extracting message');
              errorMessage = data.detail.string || String(data.detail);
              console.log('fetchProfile error - extracted message:', errorMessage);
            }
          } else {
            // Check for any error field
            console.log('fetchProfile error - checking other error fields');
            const errorKeys = Object.keys(data);
            for (const key of errorKeys) {
              const fieldError = data[key];
              console.log(`fetchProfile error - checking field ${key}:`, fieldError);
              if (fieldError) {
                if (Array.isArray(fieldError)) {
                  console.log(`fetchProfile error - field ${key} is array, taking first element`);
                  errorMessage = fieldError[0];
                  break;
                } else if (typeof fieldError === 'string') {
                  console.log(`fetchProfile error - field ${key} is string`);
                  errorMessage = fieldError;
                  break;
                } else if (typeof fieldError === 'object') {
                  console.log(`fetchProfile error - field ${key} is object, extracting message`);
                  errorMessage = fieldError.string || String(fieldError);
                  console.log(`fetchProfile error - extracted message from ${key}:`, errorMessage);
                  break;
                }
              }
            }
          }
        }
        console.log('fetchProfile error - final errorMessage:', errorMessage);
        throw new Error(errorMessage);
      }
      setForm(prev => ({
        ...prev,
        username: data.username || '',
        email: data.email || '',
        first_name: data.first_name || '',
        last_name: data.last_name || '',
        address: data.address || '',
        city: data.city || '',
        country: data.country || '',
        postal_code: data.postal_code || '',
        image: data.image || '',
      }));
    } catch (e) {
      showSnackbar(e.message || t('general.error'), { severity: 'error' });
    } finally {
      setInitialLoading(false);
    }
  };

  useEffect(() => {
    fetchProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    console.log('focusedField changed to:', focusedField);
  }, [focusedField]);

  const handleSubmit = async () => {
    if (form.new_password && form.new_password !== form.confirm_password) {
      showSnackbar(t('registration.passwords_do_not_match'), { severity: 'error' });
      return;
    }

    try {
      setLoading(true);
      const apiUrl = process.env.REACT_APP_API_URL;
      const token = localStorage.getItem('accessToken');
      const payload = {
        username: form.username,
        email: form.email,
        first_name: form.first_name,
        last_name: form.last_name,
        address: form.address,
        city: form.city,
        country: form.country,
        postal_code: form.postal_code,
        image: form.image,
      };
      if (form.new_password) {
        payload.current_password = form.current_password;
        payload.new_password = form.new_password;
      }

      const res = await fetch(`${apiUrl}authentication/superadmin/profile/patch/`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      let data;
      try {
        data = await res.json();
      } catch (parseError) {
        data = await res.text();
        // Try to parse as JSON if it's a JSON string
        if (typeof data === 'string') {
          try {
            data = JSON.parse(data);
          } catch {
            // Keep as string
          }
        }
      }

      if (!res.ok) {
        console.log('handleSubmit error - raw data:', data);
        console.log('handleSubmit error - data type:', typeof data);
        let errorMessage = 'Failed to update profile';
        if (typeof data === 'string') {
          console.log('handleSubmit error - data is string');
          errorMessage = data;
        } else if (data && typeof data === 'object') {
          console.log('handleSubmit error - data is object, keys:', Object.keys(data));
          // Check for current_password error
          if (data.current_password) {
            console.log('handleSubmit error - found current_password:', data.current_password);
            console.log('handleSubmit error - current_password type:', typeof data.current_password);
            if (Array.isArray(data.current_password)) {
              console.log('handleSubmit error - current_password is array, taking first element');
              errorMessage = data.current_password[0];
            } else if (typeof data.current_password === 'string') {
              console.log('handleSubmit error - current_password is string');
              errorMessage = data.current_password;
            } else if (typeof data.current_password === 'object') {
              console.log('handleSubmit error - current_password is object, checking .string property');
              errorMessage = data.current_password.string || String(data.current_password);
              console.log('handleSubmit error - extracted message:', errorMessage);
            }
          } else if (data.detail) {
            console.log('handleSubmit error - found detail:', data.detail);
            if (Array.isArray(data.detail)) {
              console.log('handleSubmit error - detail is array, taking first element');
              errorMessage = data.detail[0];
            } else if (typeof data.detail === 'string') {
              console.log('handleSubmit error - detail is string');
              // Check if it's a Python dict string representation
              if (data.detail.startsWith('{') && data.detail.includes('ErrorDetail')) {
                console.log('handleSubmit error - detail appears to be Python dict string, parsing...');
                try {
                  // Extract ErrorDetail string value using regex
                  const errorDetailMatch = data.detail.match(/ErrorDetail\(string='([^']+)'/);
                  if (errorDetailMatch) {
                    errorMessage = errorDetailMatch[1];
                    console.log('handleSubmit error - extracted from ErrorDetail:', errorMessage);
                  } else {
                    errorMessage = data.detail;
                  }
                } catch (parseError) {
                  console.log('handleSubmit error - failed to parse ErrorDetail string:', parseError);
                  errorMessage = data.detail;
                }
              } else {
                errorMessage = data.detail;
              }
            } else if (typeof data.detail === 'object') {
              console.log('handleSubmit error - detail is object, extracting message');
              errorMessage = data.detail.string || String(data.detail);
              console.log('handleSubmit error - extracted message:', errorMessage);
            }
          } else {
            // Check for any error field
            console.log('handleSubmit error - checking other error fields');
            const errorKeys = Object.keys(data);
            for (const key of errorKeys) {
              const fieldError = data[key];
              console.log(`handleSubmit error - checking field ${key}:`, fieldError);
              if (fieldError) {
                if (Array.isArray(fieldError)) {
                  console.log(`handleSubmit error - field ${key} is array, taking first element`);
                  errorMessage = fieldError[0];
                  break;
                } else if (typeof fieldError === 'string') {
                  console.log(`handleSubmit error - field ${key} is string`);
                  errorMessage = fieldError;
                  break;
                } else if (typeof fieldError === 'object') {
                  console.log(`handleSubmit error - field ${key} is object, extracting message`);
                  errorMessage = fieldError.string || String(fieldError);
                  console.log(`handleSubmit error - extracted message from ${key}:`, errorMessage);
                  break;
                }
              }
            }
          }
        }
        console.log('handleSubmit error - final errorMessage:', errorMessage);
        throw new Error(errorMessage);
      }

      showSnackbar(t('adminProfile.snackbarMessage'), { severity: 'success' });

      setForm(prev => ({
        ...prev,
        current_password: '',
        new_password: '',
        confirm_password: '',
      }));
      // refresh to get normalized values from backend
      fetchProfile();
    } catch (error) {
      showSnackbar(error.message || t('general.error'), { severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const getInitials = () => {
    const first = form.first_name?.charAt(0).toUpperCase() || '';
    const last = form.last_name?.charAt(0).toUpperCase() || '';
    return first + last || 'SA';
  };


  return (
    <div style={{
      padding: '24px',
      backgroundColor: '#F5F7FA',
      minHeight: '100vh',
      fontFamily: 'Poppins, -apple-system, BlinkMacSystemFont, sans-serif',
    }}>
      {/* Header Section */}
      <div style={{
        padding: '32px',
        marginBottom: '24px',
        borderRadius: '20px',
        background: `linear-gradient(135deg, ${colors.primary} 0%, #3347FF 100%)`,
        boxShadow: '0 8px 32px rgba(0, 23, 231, 0.25)',
        position: 'relative',
        overflow: 'hidden',
      }}>
        <div style={{
          position: 'absolute',
          top: '-50px',
          right: '-50px',
          width: '200px',
          height: '200px',
          background: `radial-gradient(circle, rgba(244, 225, 247, 0.2) 0%, transparent 70%)`,
          pointerEvents: 'none',
        }}></div>

        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '32px',
          position: 'relative',
          zIndex: 1,
          flexWrap: 'wrap',
        }}>
          {/* Avatar */}
          <div style={{ position: 'relative' }}>
            <ImageUploadButton
              defaultValue={form.image}
              onImageUpload={(url) => setForm(prev => ({ ...prev, image: url }))}
              shape="circle"
              size={120}
            />
          </div>

          {/* User Info */}
          <div style={{ flex: 1 }}>
            <h1 style={{
              fontWeight: 700,
              fontSize: '32px',
              color: colors.surface,
              textShadow: '0 2px 8px rgba(0,0,0,0.15)',
              margin: '0 0 8px 0',
            }}>
              {form.first_name} {form.last_name}
            </h1>
            <p style={{
              fontWeight: 400,
              fontSize: '15px',
              color: 'rgba(255, 255, 255, 0.9)',
              textShadow: '0 1px 4px rgba(0,0,0,0.1)',
              margin: '0 0 8px 0',
            }}>
              @{form.username}
            </p>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              padding: '6px 16px',
              borderRadius: '8px',
              backgroundColor: 'rgba(255, 255, 255, 0.15)',
              color: colors.accent,
              fontSize: '13px',
              fontWeight: 500,
            }}>
              👤 {t('superAdmin.layout.superAdmin')}
            </div>
          </div>

          {/* Progress Indicator */}
          <div style={{
            position: 'relative',
            width: '80px',
            height: '80px',
          }}>
            <svg width="80" height="80" style={{ transform: 'rotate(-90deg)' }}>
              <circle
                cx="40"
                cy="40"
                r="34"
                fill="none"
                stroke="rgba(244, 225, 247, 0.3)"
                strokeWidth="5"
              />
              <circle
                cx="40"
                cy="40"
                r="34"
                fill="none"
                stroke={getProgressColor(formCompletionPercentage)}
                strokeWidth="5"
                strokeDasharray={`${2 * Math.PI * 34}`}
                strokeDashoffset={`${2 * Math.PI * 34 * (1 - formCompletionPercentage / 100)}`}
                strokeLinecap="round"
                style={{ transition: 'stroke-dashoffset 0.5s ease' }}
              />
            </svg>
            <div style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              textAlign: 'center',
            }}>
              <div style={{
                fontSize: '20px',
                fontWeight: 700,
                color: colors.surface,
                textShadow: '0 2px 4px rgba(0,0,0,0.2)',
              }}>
                {formCompletionPercentage}%
              </div>
              <div style={{
                fontSize: '9px',
                fontWeight: 500,
                color: 'rgba(255, 255, 255, 0.9)',
              }}>
                Complete
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Account Information Section */}
      <div style={{
        padding: '24px',
        marginBottom: '24px',
        borderRadius: '16px',
        backgroundColor: colors.surface,
        border: `1px solid ${colors.border}`,
        boxShadow: '0 4px 16px rgba(0, 23, 231, 0.08)',
        position: 'relative',
      }}>
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '4px',
          background: `linear-gradient(90deg, ${colors.primary} 0%, ${colors.accent} 100%)`,
          borderRadius: '16px 16px 0 0',
        }}></div>

        <h2 style={{
          fontSize: '18px',
          fontWeight: 600,
          color: colors.text,
          margin: '0 0 24px 0',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
        }}>
          <span style={{ fontSize: '24px', color: colors.primary }}>👤</span>
          {t('superAdmin.profile.sections.accountInformation')}
        </h2>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: '16px',
        }}>
          <InputField label={t('superAdmin.profile.fields.username')} name="username" icon="👤" value={form.username} onChange={handleChange} isFocused={focusedField === 'username'} onFocus={() => handleFocus('username')} onBlur={handleBlur} />
          <InputField label={t('superAdmin.profile.fields.email')} name="email" type="email" icon="📧" value={form.email} onChange={handleChange} isFocused={focusedField === 'email'} onFocus={() => handleFocus('email')} onBlur={handleBlur} />
          <InputField label={t('superAdmin.profile.fields.first_name')} name="first_name" value={form.first_name} onChange={handleChange} isFocused={focusedField === 'first_name'} onFocus={() => handleFocus('first_name')} onBlur={handleBlur} />
          <InputField label={t('superAdmin.profile.fields.last_name')} name="last_name" value={form.last_name} onChange={handleChange} isFocused={focusedField === 'last_name'} onFocus={() => handleFocus('last_name')} onBlur={handleBlur} />
        </div>
      </div>

      {/* Location & Contact Section */}
      <div style={{
        padding: '24px',
        marginBottom: '24px',
        borderRadius: '16px',
        backgroundColor: colors.surface,
        border: `1px solid ${colors.border}`,
        boxShadow: '0 4px 16px rgba(0, 23, 231, 0.08)',
        position: 'relative',
      }}>
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '4px',
          background: `linear-gradient(90deg, ${colors.primary} 0%, ${colors.accent} 100%)`,
          borderRadius: '16px 16px 0 0',
        }}></div>

        <h2 style={{
          fontSize: '18px',
          fontWeight: 600,
          color: colors.text,
          margin: '0 0 24px 0',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
        }}>
          <span style={{ fontSize: '24px', color: colors.primary }}>📍</span>
          {t('superAdmin.profile.sections.locationContact')}
        </h2>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: '16px',
        }}>
          <div style={{ gridColumn: '1 / -1' }}>
            <InputField label={t('superAdmin.profile.fields.address')} name="address" value={form.address} onChange={handleChange} isFocused={focusedField === 'address'} onFocus={() => handleFocus('address')} onBlur={handleBlur} />
          </div>
          <InputField label={t('superAdmin.profile.fields.city')} name="city" value={form.city} onChange={handleChange} isFocused={focusedField === 'city'} onFocus={() => handleFocus('city')} onBlur={handleBlur} />
          <InputField label={t('superAdmin.profile.fields.country')} name="country" value={form.country} onChange={handleChange} isFocused={focusedField === 'country'} onFocus={() => handleFocus('country')} onBlur={handleBlur} />
          <InputField label={t('superAdmin.profile.fields.postal_code')} name="postal_code" value={form.postal_code} onChange={handleChange} isFocused={focusedField === 'postal_code'} onFocus={() => handleFocus('postal_code')} onBlur={handleBlur} />
        </div>
      </div>

      {/* Change Password Section */}
      <div style={{
        padding: '24px',
        marginBottom: '24px',
        borderRadius: '16px',
        backgroundColor: colors.surface,
        border: `1px solid ${colors.border}`,
        boxShadow: '0 4px 16px rgba(0, 23, 231, 0.08)',
        position: 'relative',
      }}>
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '4px',
          background: `linear-gradient(90deg, ${colors.primary} 0%, ${colors.accent} 100%)`,
          borderRadius: '16px 16px 0 0',
        }}></div>

        <h2 style={{
          fontSize: '18px',
          fontWeight: 600,
          color: colors.text,
          margin: '0 0 24px 0',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
        }}>
          <span style={{ fontSize: '24px', color: colors.primary }}>🔒</span>
          {t('superAdmin.profile.sections.changePassword')}
        </h2>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: '16px',
        }}>
          <div style={{ gridColumn: '1 / -1' }}>
            <InputField
              label={t('superAdmin.profile.fields.current_password')}
              name="current_password"
              type="password"
              icon="🔒"
              showPassword={showCurrentPassword}
              onTogglePassword={() => setShowCurrentPassword(!showCurrentPassword)}
              value={form.current_password}
              onChange={handleChange}
              isFocused={focusedField === 'current_password'}
              onFocus={() => handleFocus('current_password')}
              onBlur={handleBlur}
            />
          </div>
          <InputField
            label={t('superAdmin.profile.fields.new_password')}
            name="new_password"
            type="password"
            showPassword={showNewPassword}
            onTogglePassword={() => setShowNewPassword(!showNewPassword)}
            value={form.new_password}
            onChange={handleChange}
            isFocused={focusedField === 'new_password'}
            onFocus={() => handleFocus('new_password')}
            onBlur={handleBlur}
          />
          <InputField
            label={t('superAdmin.profile.fields.confirm_password')}
            name="confirm_password"
            type="password"
            showPassword={showConfirmPassword}
            onTogglePassword={() => setShowConfirmPassword(!showConfirmPassword)}
            value={form.confirm_password}
            onChange={handleChange}
            isFocused={focusedField === 'confirm_password'}
            onFocus={() => handleFocus('confirm_password')}
            onBlur={handleBlur}
          />
        </div>
      </div>

      {/* Action Buttons */}
      <div style={{
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '16px',
        marginTop: '24px',
      }}>
        
        <button
          onClick={handleSubmit}
          disabled={initialLoading || loading || !form.username || !form.email || !form.first_name || !form.last_name}
          style={{
            borderRadius: '12px',
            padding: '14px 28px',
            fontWeight: 600,
            fontSize: '15px',
            minWidth: '140px',
            cursor: loading ? 'not-allowed' : 'pointer',
            transition: 'all 0.3s ease',
            background: `linear-gradient(135deg, ${colors.primary} 0%, #3347FF 100%)`,
            color: colors.surface,
            border: 'none',
            boxShadow: '0 4px 15px rgba(0, 23, 231, 0.3)',
            opacity: (loading || !form.username || !form.email || !form.first_name || !form.last_name) ? 0.6 : 1,
            fontFamily: 'inherit',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
          onMouseEnter={(e) => {
            if (!loading && form.username && form.email && form.first_name && form.last_name) {
              e.target.style.background = `linear-gradient(135deg, #0012B8 0%, ${colors.primary} 100%)`;
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 6px 20px rgba(0, 23, 231, 0.4)';
            }
          }}
          onMouseLeave={(e) => {
            e.target.style.background = `linear-gradient(135deg, ${colors.primary} 0%, #3347FF 100%)`;
            e.target.style.transform = 'translateY(0)';
            e.target.style.boxShadow = '0 4px 15px rgba(0, 23, 231, 0.3)';
          }}
        >
          {loading ? (
            <>
              <div style={{
                width: '16px',
                height: '16px',
                border: `2px solid ${colors.surface}`,
                borderTopColor: 'transparent',
                borderRadius: '50%',
                animation: 'spin 0.8s linear infinite',
              }}></div>
              {t('general.loading')}
            </>
          ) : (
            <>💾 {t('adminProfile.updateButton')}</>
          )}
        </button>
      </div>

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default SuperAdminProfileEdit;


